// Angular
import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';

// RxJS
import { map } from 'rxjs/operators';

// Third-party
import { OAuthService } from 'angular-oauth2-oidc';

// Local
import { UserTypeEnum } from '@app/model/user-types.model';
import { EmulationService } from '@core/auth/emulation.service';

/**
 * Maps user types to their dedicated dashboard routes.
 * Add an entry here to give another user type its own dashboard;
 * user types without an entry land on the existing home page.
 */
export const USER_TYPE_DASHBOARD_ROUTES: Partial<Record<UserTypeEnum, string>> =
  {
    [UserTypeEnum.Attorney]: '/home/attorney',
    [UserTypeEnum.Clerk]: '/home/clerk',
    [UserTypeEnum.Judge]: '/home/judge',
    [UserTypeEnum.Form50Agent]: '/home/form50-agent',
  };

/**
 * Reads the effective user type — the emulated user's type when emulation is active, otherwise the
 * logged-in user's type from the OAuth identity claims. Emulation must take precedence so dashboard
 * dispatch reflects the user being troubleshooted, not the admin.
 */
const getEffectiveUserType = (
  oauthService: OAuthService,
  emulationService: EmulationService,
): UserTypeEnum | null => {
  if (emulationService.isEmulating()) {
    return emulationService.emulatedProfile()?.userType ?? null;
  }

  const claims = oauthService.getIdentityClaims() as {
    fsx_user_type?: UserTypeEnum;
  } | null;
  return claims?.fsx_user_type ?? null;
};

/**
 * Dispatches users landing on /home to their user-type-specific dashboard
 * when one exists. All other user types fall through to the existing home page.
 * Awaits any pending emulation restore so an emulated user is routed to their
 * dashboard rather than the admin's default after a reload.
 */
export const homeDispatchGuard: CanActivateFn = () => {
  const router = inject(Router);
  const oauthService = inject(OAuthService);
  const emulationService = inject(EmulationService);

  return emulationService.ensureReady().pipe(
    map(() => {
      const userType = getEffectiveUserType(oauthService, emulationService);
      const dashboardRoute = userType
        ? USER_TYPE_DASHBOARD_ROUTES[userType]
        : undefined;
      return dashboardRoute ? router.parseUrl(dashboardRoute) : true;
    }),
  );
};

/**
 * Release hold: the user-type dashboards are not shipped on this branch, so every
 * user lands on the legacy (iframed) home page. Sends deep links to
 * /home/attorney, /home/clerk and /home/judge back to /home.
 */
export const roleDashboardsDisabledGuard: CanActivateFn = () =>
  inject(Router).parseUrl('/home');

/**
 * Restricts the attorney dashboard route to Attorney users (including an emulated
 * attorney); anyone else (e.g. via deep link) is sent back to the default home page.
 */
export const isAttorneyUserGuard: CanActivateFn = () => {
  const router = inject(Router);
  const oauthService = inject(OAuthService);
  const emulationService = inject(EmulationService);

  return emulationService.ensureReady().pipe(
    map(() =>
      getEffectiveUserType(oauthService, emulationService) ===
      UserTypeEnum.Attorney
        ? true
        : router.parseUrl('/home'),
    ),
  );
};

/**
 * Restricts the clerk dashboard route to Clerk users (including an emulated
 * clerk); anyone else (e.g. via deep link) is sent back to the default home page.
 */
export const isClerkUserGuard: CanActivateFn = () => {
  const router = inject(Router);
  const oauthService = inject(OAuthService);
  const emulationService = inject(EmulationService);

  return emulationService.ensureReady().pipe(
    map(() =>
      getEffectiveUserType(oauthService, emulationService) ===
      UserTypeEnum.Clerk
        ? true
        : router.parseUrl('/home'),
    ),
  );
};

/**
 * Restricts the Form 50 Agent dashboard route to Form 50 Agent users (including
 * an emulated agent); anyone else (e.g. via deep link) is sent back to the
 * default home page.
 */
export const isForm50AgentUserGuard: CanActivateFn = () => {
  const router = inject(Router);
  const oauthService = inject(OAuthService);
  const emulationService = inject(EmulationService);

  return emulationService.ensureReady().pipe(
    map(() =>
      getEffectiveUserType(oauthService, emulationService) ===
      UserTypeEnum.Form50Agent
        ? true
        : router.parseUrl('/home'),
    ),
  );
};

/**
 * Restricts the Judge Review routes to Clerk or Judge users (a clerk may act on
 * behalf of a judge). Defense-in-depth: the API enforces the same check, so a
 * non-eligible user (e.g. via deep link) is sent back to the default home page
 * rather than instantiating the studio before the API rejects.
 */
export const isJudgeReviewUserGuard: CanActivateFn = () => {
  const router = inject(Router);
  const oauthService = inject(OAuthService);
  const emulationService = inject(EmulationService);

  return emulationService.ensureReady().pipe(
    map(() => {
      const userType = getEffectiveUserType(oauthService, emulationService);
      return userType === UserTypeEnum.Clerk || userType === UserTypeEnum.Judge
        ? true
        : router.parseUrl('/home');
    }),
  );
};
